import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Plus, Upload, CheckCircle, AlertTriangle, FileBarChart, 
  GitCompare, Play, Trash2, Eye, Clock, Activity, 
  ArrowRight, MessageSquare, X, ChevronDown, ChevronUp,
  RefreshCw, Loader2, Send, FileUp, UserPlus, Users, FileSpreadsheet
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Checkbox } from '@/components/ui/checkbox';

// Analysis Modal Component
function ProgramAnalysisModal({ program, projectId, open, onClose, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [showActivities, setShowActivities] = useState(false);

  useEffect(() => {
    if (open && program) {
      loadProgramDetails();
    }
  }, [open, program]);

  const loadProgramDetails = async () => {
    try {
      setLoading(true);
      const response = await api.programs.get(projectId, program.id, true);
      setActivities(response.data.activities || []);
    } catch (error) {
      toast.error('Failed to load program details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      uploaded: 'bg-slate-100 text-slate-700',
      analyzing: 'bg-blue-100 text-blue-700',
      analyzed: 'bg-green-100 text-green-700',
      approved: 'bg-emerald-100 text-emerald-700',
      superseded: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (!program) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
            <FileBarChart className="h-5 w-5" />
            Program Analysis: {program.program_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Program Info */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-sm">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Type</p>
              <p className="font-medium">{program.program_type}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Version</p>
              <p className="font-medium">{program.version}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Status</p>
              <Badge className={getStatusColor(program.status)}>{program.status}</Badge>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">File Format</p>
              <p className="font-mono text-sm">{program.file_format?.toUpperCase()}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Extraction Status</p>
              <Badge className={program.extraction_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                {program.extraction_status}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Confidence</p>
              <p className="font-medium">{program.extraction_confidence || 0}%</p>
            </div>
          </div>

          {/* Metrics */}
          {program.total_activities > 0 && (
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white border rounded-sm p-4 text-center">
                <p className="text-xs font-bold uppercase text-slate-500">Total Activities</p>
                <p className="text-2xl font-bold">{program.total_activities}</p>
              </div>
              <div className="bg-white border rounded-sm p-4 text-center">
                <p className="text-xs font-bold uppercase text-red-500">Critical</p>
                <p className="text-2xl font-bold text-red-600">{program.critical_activities_count || 0}</p>
              </div>
              <div className="bg-white border rounded-sm p-4 text-center">
                <p className="text-xs font-bold uppercase text-slate-500">Progress</p>
                <p className="text-2xl font-bold">{(program.overall_progress || 0).toFixed(1)}%</p>
              </div>
              <div className="bg-white border rounded-sm p-4 text-center">
                <p className="text-xs font-bold uppercase text-slate-500">Duration</p>
                <p className="text-2xl font-bold">{program.critical_path_length || '-'} days</p>
              </div>
            </div>
          )}

          {/* Extraction Notes */}
          {program.extraction_notes?.length > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
              <p className="text-xs font-bold uppercase text-amber-700 mb-2">Extraction Notes</p>
              <ul className="list-disc list-inside text-sm text-amber-800 space-y-1">
                {program.extraction_notes.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t">
            {program.extraction_status !== 'completed' && (
              <p className="text-sm text-amber-600 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Upload a data file (XML/Excel/CSV) from the program card to run analysis.
              </p>
            )}
            {program.extraction_status === 'completed' && (
              <Badge className="bg-green-100 text-green-700">Analysis Complete</Badge>
            )}
            <Button
              variant="outline"
              onClick={() => setShowActivities(!showActivities)}
              disabled={activities.length === 0}
            >
              <Eye className="h-4 w-4 mr-2" />
              {showActivities ? 'Hide' : 'Show'} Activities ({activities.length})
            </Button>
          </div>

          {/* Activities Table */}
          {showActivities && activities.length > 0 && (
            <div className="border rounded-sm overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-bold text-xs uppercase">ID</th>
                      <th className="text-left p-2 font-bold text-xs uppercase">Activity</th>
                      <th className="text-left p-2 font-bold text-xs uppercase">Start</th>
                      <th className="text-left p-2 font-bold text-xs uppercase">Finish</th>
                      <th className="text-right p-2 font-bold text-xs uppercase">Dur</th>
                      <th className="text-right p-2 font-bold text-xs uppercase">Float</th>
                      <th className="text-right p-2 font-bold text-xs uppercase">Prev %</th>
                      <th className="text-right p-2 font-bold text-xs uppercase">Curr %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((act, idx) => (
                      <tr key={act.id || idx} className={`border-t ${act.is_critical ? 'bg-red-50' : ''}`}>
                        <td className="p-2 font-mono text-xs">{act.activity_code || act.activity_id}</td>
                        <td className="p-2">
                          {act.activity_name}
                          {act.is_critical && <Badge className="ml-2 bg-red-100 text-red-700 text-xs">Critical</Badge>}
                        </td>
                        <td className="p-2 font-mono text-xs">{act.planned_start || '-'}</td>
                        <td className="p-2 font-mono text-xs">{act.planned_finish || '-'}</td>
                        <td className="p-2 text-right font-mono">{act.original_duration || '-'}</td>
                        <td className={`p-2 text-right font-mono ${(act.total_float != null && act.total_float <= 0) ? 'text-red-600 font-bold' : ''}`}>
                          {act.total_float ?? '-'}
                        </td>
                        <td className="p-2 text-right font-mono text-slate-400">{act.previous_percent_complete ?? act.percent_complete ?? 0}%</td>
                        <td className={`p-2 text-right font-mono font-bold ${(act.current_percent_complete ?? act.percent_complete ?? 0) === 100 ? 'text-emerald-600' : (act.current_percent_complete ?? act.percent_complete ?? 0) > 0 ? 'text-blue-600' : ''}`}>
                          {act.current_percent_complete ?? act.percent_complete ?? 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Comparison Modal Component
function ComparisonModal({ comparison, projectId, open, onClose, onRefresh }) {
  const [delays, setDelays] = useState([]);
  const [selectedDelay, setSelectedDelay] = useState(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentForm, setCommentForm] = useState({
    contractor_comment: '',
    delay_cause: '',
    responsible_party_comment: ''
  });
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (comparison) {
      setDelays(comparison.delays || []);
    }
  }, [comparison]);

  const handleComment = async () => {
    if (!selectedDelay) return;
    try {
      await api.programs.commentOnDelay(projectId, comparison.id, selectedDelay.id, commentForm);
      toast.success('Comment added');
      setCommentDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleConvertToEvent = async (delay) => {
    if (!window.confirm('Convert this delay to a Delay Event? This will create a new event record.')) return;
    try {
      setConverting(true);
      const response = await api.programs.convertDelayToEvent(projectId, comparison.id, delay.id);
      toast.success(`Delay event created: ${response.data.event_id}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  if (!comparison) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Program Comparison Results
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Comparison Summary */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-sm">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Base Program</p>
              <p className="font-medium">{comparison.base_program_name}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Compare Program</p>
              <p className="font-medium">{comparison.compare_program_name}</p>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-white border rounded-sm p-4 text-center">
              <p className="text-xs font-bold uppercase text-slate-500">Total Delays</p>
              <p className="text-2xl font-bold text-red-600">{delays.length}</p>
            </div>
            <div className="bg-white border rounded-sm p-4 text-center">
              <p className="text-xs font-bold uppercase text-slate-500">Total Slip Days</p>
              <p className="text-2xl font-bold">{comparison.total_delay_days}</p>
            </div>
            <div className="bg-white border rounded-sm p-4 text-center">
              <p className="text-xs font-bold uppercase text-slate-500">Activities Added</p>
              <p className="text-2xl font-bold text-green-600">+{comparison.activities_added}</p>
            </div>
            <div className="bg-white border rounded-sm p-4 text-center">
              <p className="text-xs font-bold uppercase text-slate-500">Activities Removed</p>
              <p className="text-2xl font-bold text-amber-600">-{comparison.activities_removed}</p>
            </div>
            <div className="bg-white border rounded-sm p-4 text-center">
              <p className="text-xs font-bold uppercase text-slate-500">Float Eroded</p>
              <p className="text-2xl font-bold">{comparison.activities_with_float_change}</p>
            </div>
          </div>

          {/* AI Summary */}
          {comparison.ai_summary && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-sm">
              <p className="text-xs font-bold uppercase text-blue-700 mb-2">AI Analysis Summary</p>
              <p className="text-sm text-blue-900">{comparison.ai_summary}</p>
              {comparison.ai_recommendations?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-bold uppercase text-blue-700 mb-1">Recommendations</p>
                  <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
                    {comparison.ai_recommendations.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Delays Table */}
          <div>
            <h3 className="text-sm font-bold uppercase text-slate-500 mb-3">Identified Delays</h3>
            {delays.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No delays identified</p>
            ) : (
              <div className="border rounded-sm overflow-hidden">
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-bold text-xs uppercase">Activity</th>
                        <th className="text-left p-2 font-bold text-xs uppercase">Type</th>
                        <th className="text-right p-2 font-bold text-xs uppercase">Days</th>
                        <th className="text-left p-2 font-bold text-xs uppercase">Impact</th>
                        <th className="text-left p-2 font-bold text-xs uppercase">Comment</th>
                        <th className="text-center p-2 font-bold text-xs uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {delays.map((delay, idx) => (
                        <tr key={delay.id || idx} className={`border-t ${delay.affects_critical_path ? 'bg-red-50' : ''}`}>
                          <td className="p-2">
                            <div>
                              <p className="font-medium">{delay.activity_name}</p>
                              <p className="text-xs text-slate-500 font-mono">{delay.activity_code || delay.activity_id}</p>
                            </div>
                          </td>
                          <td className="p-2">
                            <Badge variant="outline" className="text-xs">
                              {delay.delay_type?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="p-2 text-right font-mono font-bold text-red-600">
                            +{delay.delay_days}
                          </td>
                          <td className="p-2">
                            {delay.affects_critical_path && (
                              <Badge className="bg-red-100 text-red-700 text-xs">Critical Path</Badge>
                            )}
                          </td>
                          <td className="p-2">
                            {delay.contractor_comment ? (
                              <p className="text-xs text-slate-600 truncate max-w-[150px]" title={delay.contractor_comment}>
                                {delay.contractor_comment}
                              </p>
                            ) : (
                              <span className="text-xs text-slate-400">No comment</span>
                            )}
                          </td>
                          <td className="p-2">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedDelay(delay);
                                  setCommentForm({
                                    contractor_comment: delay.contractor_comment || '',
                                    delay_cause: delay.delay_cause || '',
                                    responsible_party_comment: delay.responsible_party_comment || ''
                                  });
                                  setCommentDialogOpen(true);
                                }}
                                title="Add Comment"
                              >
                                <MessageSquare className="h-4 w-4" />
                              </Button>
                              {!delay.converted_to_delay_event && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleConvertToEvent(delay)}
                                  disabled={converting}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Convert to Delay Event"
                                >
                                  <ArrowRight className="h-4 w-4" />
                                </Button>
                              )}
                              {delay.converted_to_delay_event && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Converted</Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Comment Dialog */}
        <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-barlow font-bold uppercase">
                Comment on Delay: {selectedDelay?.activity_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Contractor Comment *</Label>
                <Textarea
                  value={commentForm.contractor_comment}
                  onChange={(e) => setCommentForm({ ...commentForm, contractor_comment: e.target.value })}
                  rows={3}
                  className="rounded-sm resize-none"
                  placeholder="Explain the cause and impact of this delay..."
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Delay Cause</Label>
                <Select value={commentForm.delay_cause} onValueChange={(v) => setCommentForm({ ...commentForm, delay_cause: v })}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select cause" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_instruction">Client Instruction</SelectItem>
                    <SelectItem value="late_information">Late Information</SelectItem>
                    <SelectItem value="design_change">Design Change</SelectItem>
                    <SelectItem value="weather">Weather</SelectItem>
                    <SelectItem value="site_conditions">Unforeseen Site Conditions</SelectItem>
                    <SelectItem value="material_shortage">Material Shortage</SelectItem>
                    <SelectItem value="subcontractor">Sub-contractor Default</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Responsible Party Comment</Label>
                <Input
                  value={commentForm.responsible_party_comment}
                  onChange={(e) => setCommentForm({ ...commentForm, responsible_party_comment: e.target.value })}
                  className="rounded-sm"
                  placeholder="e.g., Architect, Client, Sub-contractor XYZ"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleComment} className="bg-slate-900 text-white hover:bg-slate-800">
                  Save Comment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function ProgramManager({ projectId }) {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [programFormats, setProgramFormats] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [nativeUploadOpen, setNativeUploadOpen] = useState(false);
  const [nativeUploadTarget, setNativeUploadTarget] = useState(null);
  const [isUploadingNative, setIsUploadingNative] = useState(false);
  const [submitUpdateOpen, setSubmitUpdateOpen] = useState(false);
  const [submitUpdateTarget, setSubmitUpdateTarget] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateRecipients, setUpdateRecipients] = useState([]);
  const [updateNotes, setUpdateNotes] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [professionalTeam, setProfessionalTeam] = useState([]);

  const [isUploading, setIsUploading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dataUploadOpen, setDataUploadOpen] = useState(false);
  const [dataUploadTarget, setDataUploadTarget] = useState(null);
  const [isUploadingData, setIsUploadingData] = useState(false);

  const [uploadForm, setUploadForm] = useState({
    file: null,
    program_name: '',
    program_type: 'progress_update',
    version: '',
    subcontractor_id: '',
    data_date: ''
  });

  const [compareForm, setCompareForm] = useState({
    base_program_id: '',
    compare_program_id: ''
  });

  const normalizedRole = user?.role?.toLowerCase()?.replace(/\s+/g, '_') || '';
  const canUpload = ['admin', 'contracts_manager', 'contractor', 'planner', 'site_agent'].includes(normalizedRole);
  const canApprove = ['admin', 'contracts_manager'].includes(normalizedRole);
  const canDelete = ['admin', 'contracts_manager'].includes(normalizedRole);

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [programsRes, typesRes, formatsRes, scRes, compRes, teamRes] = await Promise.allSettled([
        api.programs.list(projectId),
        api.programs.getTypes(),
        api.programs.getFormats(),
        api.subcontractors.getAll(projectId),
        api.programs.getComparisons(projectId),
        api.professionalTeam.getAll(projectId)
      ]);
      setPrograms(programsRes.status === 'fulfilled' ? (programsRes.value.data?.programs || programsRes.value.data || []) : []);
      setProgramTypes(typesRes.status === 'fulfilled' ? (typesRes.value.data || []) : []);
      setProgramFormats(formatsRes.status === 'fulfilled' ? (formatsRes.value.data || []) : []);
      setSubcontractors(scRes.status === 'fulfilled' ? (scRes.value.data || []) : []);
      setComparisons(compRes.status === 'fulfilled' ? (compRes.value.data || []) : []);
      setProfessionalTeam(teamRes.status === 'fulfilled' ? (teamRes.value.data || []) : []);
    } catch (error) {
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('program_name', uploadForm.program_name);
      formData.append('program_type', uploadForm.program_type);
      formData.append('version', uploadForm.version);
      if (uploadForm.subcontractor_id) formData.append('subcontractor_id', uploadForm.subcontractor_id);
      if (uploadForm.data_date) formData.append('data_date', uploadForm.data_date);

      await api.programs.upload(projectId, formData);
      toast.success('Program uploaded successfully');
      setUploadDialogOpen(false);
      setUploadForm({ file: null, program_name: '', program_type: 'progress_update', version: '', subcontractor_id: '', data_date: '' });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
    toast.success('Programs refreshed');
  };

  const handleUploadNative = async (e) => {
    e.preventDefault();
    if (!nativeUploadTarget) return;
    const fileInput = document.getElementById('native-file-input');
    const file = fileInput?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }
    try {
      setIsUploadingNative(true);
      const formData = new FormData();
      formData.append('file', file);
      await api.programs.uploadNative(projectId, nativeUploadTarget.id, formData);
      toast.success(`Native file ${file.name} uploaded`);
      setNativeUploadOpen(false);
      setNativeUploadTarget(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploadingNative(false);
    }
  };

  const handleUploadData = async (e) => {
    e.preventDefault();
    if (!dataUploadTarget) return;
    const fileInput = document.getElementById('data-file-input');
    const file = fileInput?.files?.[0];
    if (!file) { toast.error('Please select a file'); return; }
    try {
      setIsUploadingData(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.programs.uploadData(projectId, dataUploadTarget.id, formData);
      if (response.data.success === false) {
        toast.error(response.data.error || 'Failed to parse data file');
      } else {
        toast.success(`Data file analyzed: ${response.data.total_activities || 0} activities extracted`);
      }
      setDataUploadOpen(false);
      setDataUploadTarget(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Data file upload failed');
    } finally {
      setIsUploadingData(false);
    }
  };

  const handleAddRecipient = () => {
    if (!recipientEmail) return;
    if (updateRecipients.some(r => r.email === recipientEmail)) {
      toast.error('Recipient already added'); return;
    }
    setUpdateRecipients([...updateRecipients, { email: recipientEmail, name: recipientName || recipientEmail.split('@')[0], is_cc: false }]);
    setRecipientEmail('');
    setRecipientName('');
  };

  const handleAddTeamMember = (member) => {
    const email = member.email || member.contact_email;
    if (!email) { toast.error('No email for this team member'); return; }
    if (updateRecipients.some(r => r.email === email)) return;
    setUpdateRecipients([...updateRecipients, { email, name: member.company_name || member.name || email.split('@')[0], is_cc: false }]);
  };

  const handleRemoveRecipient = (email) => {
    setUpdateRecipients(updateRecipients.filter(r => r.email !== email));
  };

  const handleToggleCC = (email) => {
    setUpdateRecipients(updateRecipients.map(r => r.email === email ? { ...r, is_cc: !r.is_cc } : r));
  };

  const handleSubmitUpdate = async () => {
    if (!submitUpdateTarget) return;
    const toRecipients = updateRecipients.filter(r => !r.is_cc);
    if (toRecipients.length === 0) {
      toast.error('Add at least one recipient (not CC)'); return;
    }
    try {
      setIsSubmitting(true);
      await api.programs.submitUpdate(projectId, submitUpdateTarget.id, {
        recipients: updateRecipients,
        cc_recipients: [],
        notes: updateNotes,
      });
      toast.success('Progress update submitted successfully');
      setSubmitUpdateOpen(false);
      setSubmitUpdateTarget(null);
      setUpdateRecipients([]);
      setUpdateNotes('');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompare = async (e) => {
    e.preventDefault();
    try {
      const response = await api.programs.compare(
        projectId,
        compareForm.base_program_id,
        compareForm.compare_program_id
      );
      toast.success(`Comparison complete: ${response.data.total_delays} delays identified`);
      setCompareDialogOpen(false);
      loadData();
      // Show the comparison results
      const comparisonData = await api.programs.getComparison(projectId, response.data.comparison_id);
      setSelectedComparison(comparisonData.data);
      setComparisonModalOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Comparison failed');
    }
  };

  const handleApproveBaseline = async (programId) => {
    if (!window.confirm('Approve this program as the project baseline? This will supersede any existing baseline.')) return;
    try {
      await api.programs.approveBaseline(projectId, programId);
      toast.success('Program approved as baseline');
      loadData();
    } catch (error) {
      toast.error('Failed to approve baseline');
    }
  };

  const handleDelete = async (programId, name) => {
    if (!window.confirm(`Delete program "${name}"? This cannot be undone.`)) return;
    try {
      await api.programs.delete(projectId, programId);
      toast.success('Program deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      uploaded: 'bg-slate-100 text-slate-700 border-slate-200',
      analyzing: 'bg-blue-100 text-blue-700 border-blue-200',
      analyzed: 'bg-green-100 text-green-700 border-green-200',
      approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      superseded: 'bg-amber-100 text-amber-700 border-amber-200',
      rejected: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getTypeColor = (type) => {
    const colors = {
      baseline: 'bg-purple-100 text-purple-700',
      progress_update: 'bg-blue-100 text-blue-700',
      impact_program: 'bg-red-100 text-red-700',
      recovery_program: 'bg-amber-100 text-amber-700',
      subcontractor: 'bg-cyan-100 text-cyan-700',
      as_built: 'bg-green-100 text-green-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  const baseline = programs.find(p => p.is_approved_baseline);
  const analyzedPrograms = programs.filter(p => p.extraction_status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading programs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-lg sm:text-2xl font-barlow font-semibold uppercase">Program Management</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            {programs.length} program{programs.length !== 1 ? 's' : ''} uploaded
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={handleRefresh}
            disabled={isRefreshing}
            data-testid="refresh-programs-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {analyzedPrograms.length >= 2 && (
            <Button 
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setCompareDialogOpen(true)}
              data-testid="compare-programs-btn"
            >
              <GitCompare className="h-3.5 w-3.5 mr-1.5" />
              <span className="hidden sm:inline">Compare Programs</span>
              <span className="sm:hidden">Compare</span>
            </Button>
          )}
          {canUpload && (
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs h-8 px-4" data-testid="upload-program-btn">
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-xl font-barlow font-bold uppercase">Upload Program</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Program File *</Label>
                    <Input
                      type="file"
                      accept=".mpp,.xer,.ccs,.xml,.xlsx,.csv,.pdf"
                      onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                      className="rounded-sm"
                      data-testid="program-file-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Supported: {programFormats.join(', ').toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Program Name *</Label>
                    <Input
                      value={uploadForm.program_name}
                      onChange={(e) => setUploadForm({ ...uploadForm, program_name: e.target.value })}
                      required
                      className="rounded-sm"
                      placeholder="e.g., Master Programme Rev 3"
                      data-testid="program-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Type *</Label>
                      <Select value={uploadForm.program_type} onValueChange={(v) => setUploadForm({ ...uploadForm, program_type: v })}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {programTypes.map((type) => (
                            <SelectItem key={type} value={type}>{type.replace('_', ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Version *</Label>
                      <Input
                        value={uploadForm.version}
                        onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })}
                        required
                        className="rounded-sm"
                        placeholder="e.g., Rev 3, Update 5"
                        data-testid="program-version-input"
                      />
                    </div>
                  </div>
                  {uploadForm.program_type === 'subcontractor' && subcontractors.length > 0 && (
                    <div>
                      <Label className="text-xs font-bold uppercase text-slate-500">Sub-Contractor</Label>
                      <Select value={uploadForm.subcontractor_id} onValueChange={(v) => setUploadForm({ ...uploadForm, subcontractor_id: v })}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Select sub-contractor" />
                        </SelectTrigger>
                        <SelectContent>
                          {subcontractors.map((sc) => (
                            <SelectItem key={sc.id} value={sc.id}>{sc.company_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs font-bold uppercase text-slate-500">Data Date</Label>
                    <Input
                      type="date"
                      value={uploadForm.data_date}
                      onChange={(e) => setUploadForm({ ...uploadForm, data_date: e.target.value })}
                      className="rounded-sm font-mono"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={isUploading}>Cancel</Button>
                    <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" disabled={isUploading} data-testid="submit-upload-btn">
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Current Baseline Banner */}
      {baseline && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-bold text-emerald-800 text-sm sm:text-base truncate">Baseline: {baseline.program_name}</p>
              <p className="text-xs sm:text-sm text-emerald-600">v{baseline.version} | Approved {baseline.approved_at ? new Date(baseline.approved_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            className="self-end sm:self-auto"
            onClick={() => { setSelectedProgram(baseline); setAnalysisModalOpen(true); }}
          >
            <Eye className="h-4 w-4 mr-2" />
            View
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs font-bold uppercase text-slate-500">Total</p>
          <p className="text-xl sm:text-2xl font-bold">{programs.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs font-bold uppercase text-green-600">Analyzed</p>
          <p className="text-xl sm:text-2xl font-bold text-green-600">{analyzedPrograms.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs font-bold uppercase text-amber-600">Pending</p>
          <p className="text-xl sm:text-2xl font-bold text-amber-600">
            {programs.filter(p => p.extraction_status === 'pending').length}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4 hidden sm:block">
          <p className="text-[10px] sm:text-xs font-bold uppercase text-purple-600">Comparisons</p>
          <p className="text-xl sm:text-2xl font-bold text-purple-600">{comparisons.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4 hidden sm:block">
          <p className="text-[10px] sm:text-xs font-bold uppercase text-red-600">Delays</p>
          <p className="text-xl sm:text-2xl font-bold text-red-600">
            {comparisons.reduce((sum, c) => sum + (c.delays?.length || 0), 0)}
          </p>
        </div>
      </div>

      {/* Programs List */}
      {programs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
          <FileBarChart className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-lg font-barlow font-bold uppercase text-slate-500 mb-2">No Programs Uploaded</h3>
          <p className="text-sm text-slate-400 mb-4">Upload construction programs to track progress and identify delays.</p>
          {canUpload && (
            <Button
              onClick={() => setUploadDialogOpen(true)}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
              data-testid="upload-program-empty-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Programme
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {programs.map((program) => (
            <div key={program.id} className="bg-white border border-slate-200 rounded-sm shadow-sm p-3 sm:p-4" data-testid={`program-${program.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
                  <div className={`rounded-full p-2 flex-shrink-0 ${program.is_approved_baseline ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                    <FileBarChart className={`h-4 w-4 sm:h-5 sm:w-5 ${program.is_approved_baseline ? 'text-emerald-600' : 'text-slate-600'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h4 className="font-bold text-sm sm:text-base truncate">{program.program_name}</h4>
                      <Badge className={`text-[10px] ${getTypeColor(program.program_type)}`}>{program.program_type.replace('_', ' ')}</Badge>
                      <Badge className={`text-[10px] ${getStatusColor(program.status)}`}>{program.status}</Badge>
                      {program.is_approved_baseline && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                          <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                          Baseline
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate">
                      v{program.version} | {program.file_format?.toUpperCase()}
                      {program.total_activities > 0 && ` | ${program.total_activities} activities`}
                      {program.native_file_name && <span className="text-indigo-600 font-medium"> | {program.native_file_format?.toUpperCase()}</span>}
                      {program.data_file_name && <span className="text-green-600 font-medium"> | {program.data_file_format?.toUpperCase()} data</span>}
                      {' '}| {new Date(program.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap pl-9 sm:pl-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => { setSelectedProgram(program); setAnalysisModalOpen(true); }}
                    data-testid={`view-program-${program.id}`}
                  >
                    <Eye className="h-3.5 w-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">View / Analyze</span>
                    <span className="sm:hidden">View</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => { setNativeUploadTarget(program); setNativeUploadOpen(true); }}
                    data-testid={`upload-native-${program.id}`}
                  >
                    <FileUp className="h-3.5 w-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">{program.native_file_name ? 'Replace Native' : 'Upload Native'}</span>
                    <span className="sm:hidden">Native</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 text-xs ${program.data_file_name ? 'text-green-600 hover:text-green-700' : 'text-amber-600 hover:text-amber-700'}`}
                    onClick={() => { setDataUploadTarget(program); setDataUploadOpen(true); }}
                    data-testid={`upload-data-${program.id}`}
                  >
                    <FileSpreadsheet className="h-3.5 w-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">{program.data_file_name ? 'Replace Data' : 'Upload Data'}</span>
                    <span className="sm:hidden">Data</span>
                  </Button>
                  {program.extraction_status === 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs text-blue-600 hover:text-blue-700"
                      onClick={() => { setSubmitUpdateTarget(program); setSubmitUpdateOpen(true); setUpdateRecipients([]); setUpdateNotes(''); }}
                      data-testid={`submit-update-${program.id}`}
                    >
                      <Send className="h-3.5 w-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">Submit Update</span>
                      <span className="sm:hidden">Send</span>
                    </Button>
                  )}
                  {canApprove && !program.is_approved_baseline && program.status === 'analyzed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs text-emerald-600 hover:text-emerald-700"
                      onClick={() => handleApproveBaseline(program.id)}
                    >
                      <CheckCircle className="h-3.5 w-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">Approve</span>
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(program.id, program.program_name)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Comparisons */}
      {comparisons.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-barlow font-bold uppercase mb-4">Recent Comparisons</h3>
          <div className="space-y-2">
            {comparisons.slice(0, 5).map((comp) => (
              <div
                key={comp.id}
                className="bg-white border border-slate-200 rounded-sm p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={async () => {
                  const response = await api.programs.getComparison(projectId, comp.id);
                  setSelectedComparison(response.data);
                  setComparisonModalOpen(true);
                }}
                data-testid={`comparison-${comp.id}`}
              >
                <div className="flex items-center gap-4">
                  <GitCompare className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="font-medium">{comp.base_program_name} vs {comp.compare_program_name}</p>
                    <p className="text-sm text-slate-500">
                      {comp.delays?.length || 0} delays | {comp.total_delay_days || 0} slip days | 
                      {new Date(comp.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Badge className={comp.total_delay_days > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                  {comp.total_delay_days > 0 ? `+${comp.total_delay_days} days` : 'On Track'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compare Programs Dialog */}
      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">Compare Programs</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCompare} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Base Program (Earlier) *</Label>
              <Select value={compareForm.base_program_id} onValueChange={(v) => setCompareForm({ ...compareForm, base_program_id: v })}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select base program" />
                </SelectTrigger>
                <SelectContent>
                  {analyzedPrograms.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.program_name} (v{p.version})
                      {p.is_approved_baseline && ' [Baseline]'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Compare Program (Later) *</Label>
              <Select value={compareForm.compare_program_id} onValueChange={(v) => setCompareForm({ ...compareForm, compare_program_id: v })}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select comparison program" />
                </SelectTrigger>
                <SelectContent>
                  {analyzedPrograms.filter(p => p.id !== compareForm.base_program_id).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.program_name} (v{p.version})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setCompareDialogOpen(false)}>Cancel</Button>
              <Button 
                type="submit" 
                className="bg-slate-900 text-white hover:bg-slate-800"
                disabled={!compareForm.base_program_id || !compareForm.compare_program_id}
              >
                <GitCompare className="h-4 w-4 mr-2" />
                Run Comparison
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Analysis Modal */}
      <ProgramAnalysisModal
        program={selectedProgram}
        projectId={projectId}
        open={analysisModalOpen}
        onClose={() => { setAnalysisModalOpen(false); setSelectedProgram(null); }}
        onRefresh={loadData}
      />

      {/* Comparison Modal */}
      <ComparisonModal
        comparison={selectedComparison}
        projectId={projectId}
        open={comparisonModalOpen}
        onClose={() => { setComparisonModalOpen(false); setSelectedComparison(null); }}
        onRefresh={loadData}
      />

      {/* Upload Native File Dialog */}
      <Dialog open={nativeUploadOpen} onOpenChange={(v) => { setNativeUploadOpen(v); if (!v) setNativeUploadTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">Upload Native File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadNative} className="space-y-4">
            <p className="text-sm text-slate-500">
              Upload the native planning software file for <strong>{nativeUploadTarget?.program_name}</strong>.
            </p>
            <p className="text-xs text-slate-400">
              Supported: Candy (.ccs), MS Project (.msp/.mpp), Primavera (.xer/.xml), Asta Powerproject (.pp/.plf)
            </p>
            {nativeUploadTarget?.native_file_name && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 text-sm">
                Current: <strong>{nativeUploadTarget.native_file_name}</strong> ({nativeUploadTarget.native_file_format?.toUpperCase()})
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Select File *</Label>
              <Input
                id="native-file-input"
                type="file"
                accept=".ccs,.msp,.mpp,.xer,.xml,.pp,.plf"
                className="rounded-sm"
                data-testid="native-file-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setNativeUploadOpen(false)} disabled={isUploadingNative}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" disabled={isUploadingNative} data-testid="submit-native-upload-btn">
                {isUploadingNative ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>) : (<><FileUp className="h-4 w-4 mr-2" />Upload</>)}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Data File Dialog */}
      <Dialog open={dataUploadOpen} onOpenChange={(v) => { setDataUploadOpen(v); if (!v) setDataUploadTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">Upload Data File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadData} className="space-y-4">
            <p className="text-sm text-slate-500">
              Upload a schedule data file for <strong>{dataUploadTarget?.program_name}</strong> to run deterministic analysis.
            </p>
            <p className="text-xs text-slate-400">
              Supported: Excel (.xlsx), XML (P6/MSP/Candy), CSV (.csv)
            </p>
            {dataUploadTarget?.data_file_name && (
              <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-sm">
                Current: <strong>{dataUploadTarget.data_file_name}</strong> ({dataUploadTarget.data_file_format?.toUpperCase()})
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Select File *</Label>
              <Input
                id="data-file-input"
                type="file"
                accept=".xml,.xlsx,.xls,.csv"
                className="rounded-sm"
                data-testid="data-file-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDataUploadOpen(false)} disabled={isUploadingData}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" disabled={isUploadingData} data-testid="submit-data-upload-btn">
                {isUploadingData ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing...</>) : (<><FileSpreadsheet className="h-4 w-4 mr-2" />Upload & Analyze</>)}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Submit Progress Update Dialog */}
      <Dialog open={submitUpdateOpen} onOpenChange={(v) => { setSubmitUpdateOpen(v); if (!v) { setSubmitUpdateTarget(null); setUpdateRecipients([]); setUpdateNotes(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">Submit Progress Update</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <p className="text-sm"><strong>Programme:</strong> {submitUpdateTarget?.program_name} (v{submitUpdateTarget?.version})</p>
              <p className="text-xs text-slate-500 mt-1">Activities: {submitUpdateTarget?.total_activities || 0} | Status: {submitUpdateTarget?.status}</p>
              <p className="text-xs text-slate-400 mt-1">A comparison will be generated against the previously submitted programme. The progress report PDF and programme file will be attached.</p>
            </div>

            {/* Recipients Section - RFI Style */}
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500 mb-2 block">Recipients</Label>

              {/* Add from Professional Team */}
              {professionalTeam.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-slate-400 mb-2">Select from Professional Team:</p>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-slate-200 rounded-sm p-2">
                    {professionalTeam.map((member) => {
                      const email = member.email || member.contact_email;
                      const isSelected = updateRecipients.some(r => r.email === email);
                      return (
                        <div
                          key={member.id}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer text-sm ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-slate-100 hover:bg-slate-50'}`}
                          onClick={() => isSelected ? handleRemoveRecipient(email) : handleAddTeamMember(member)}
                          data-testid={`team-member-${member.id}`}
                        >
                          <Users className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          <div className="truncate">
                            <span className="font-medium">{member.company_name || member.name}</span>
                            {member.discipline && <span className="text-slate-400 text-xs ml-1">({member.discipline})</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manual email entry */}
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Name"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="rounded-sm flex-1"
                  data-testid="recipient-name-input"
                />
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="rounded-sm flex-[2]"
                  data-testid="recipient-email-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(); } }}
                />
                <Button type="button" variant="outline" size="sm" onClick={handleAddRecipient} data-testid="add-recipient-btn">
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>

              {/* Recipient List */}
              {updateRecipients.length > 0 && (
                <div className="border border-slate-200 rounded-sm divide-y">
                  {updateRecipients.map((r) => (
                    <div key={r.email} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${r.is_cc ? 'bg-slate-100 text-slate-500' : 'bg-blue-100 text-blue-700'}`}>
                          {r.is_cc ? 'CC' : 'TO'}
                        </span>
                        <span className="font-medium">{r.name}</span>
                        <span className="text-slate-400">{r.email}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => handleToggleCC(r.email)}>
                          {r.is_cc ? 'Move to TO' : 'Move to CC'}
                        </Button>
                        <Button type="button" variant="ghost" size="sm" className="h-6 text-red-500" onClick={() => handleRemoveRecipient(r.email)}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {updateRecipients.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-3 border border-dashed border-slate-200 rounded-sm">No recipients added yet</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Additional Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes or comments for the recipients..."
                value={updateNotes}
                onChange={(e) => setUpdateNotes(e.target.value)}
                className="rounded-sm mt-1"
                rows={3}
                data-testid="update-notes-input"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setSubmitUpdateOpen(false)} disabled={isSubmitting}>Cancel</Button>
              <Button
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={handleSubmitUpdate}
                disabled={isSubmitting || updateRecipients.filter(r => !r.is_cc).length === 0}
                data-testid="submit-progress-update-btn"
              >
                {isSubmitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>) : (<><Send className="h-4 w-4 mr-2" />Submit Update</>)}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
